import {
  EMPTY_ARRAY,
  extractColumns,
  getAgGridColId,
  isColumnGroup,
  type ColumnDef,
  type ColumnOrColumnGroup,
} from '@talos/kyoko';
import type { ColDef, ValueGetterParams } from 'ag-grid-community';
import { useMemo, useRef } from 'react';
import type { MarketSelectorColumnContribution } from '../types';
import {
  MarketSelectorBlotterCheckboxCellRenderer,
  type MarketSelectorCheckboxValue,
} from './MarketSelectorBlotterCheckboxCellRenderer';
import { MarketSelectorBlotterHeaderComponent } from './MarketSelectorBlotterCheckboxHeaderComponent';
import type { MarketSelectorRow } from './types';

export const MARKET_SELECTOR_CHECKBOX_AGG_FUNC = 'marketSelectorCheckbox';
export const MARKET_SELECTOR_CHECKBOX_COLID = 'checkbox';

type UseMarketSelectorColumnsParams = {
  advancedMode: boolean;
  enrichments: MarketSelectorColumnContribution[];
  extensions: MarketSelectorColumnContribution[];
};

/**
 * Builds the Market Selector blotter column definitions from static base columns plus dynamic enrichment/extension columns.
 *
 * Enrichments and extensions often update their `dataMap`, render callbacks, or selection state while their column definitions
 * remain unchanged. This hook intentionally projects each contribution down to only the fields that affect column shape and
 * visibility before memoizing the final column array.
 */
export function useMarketSelectorColumns({
  advancedMode,
  enrichments,
  extensions,
}: UseMarketSelectorColumnsParams): ColumnOrColumnGroup[] {
  const columnContributionInputs = useStableColumnContributionInputs([...enrichments, ...extensions]);

  return useMemo(() => {
    const columns = [...BASE_COLUMNS, ...getContributionColumns(advancedMode, columnContributionInputs)];
    assertUniqueColumnIDs(columns);
    return columns;
  }, [advancedMode, columnContributionInputs]);
}

const BASE_COLUMNS: ColumnOrColumnGroup[] = [
  {
    type: 'custom',
    id: MARKET_SELECTOR_CHECKBOX_COLID,
    field: 'selected',
    pinned: 'left',
    params: {
      lockPinned: true,
      suppressColumnsToolPanel: true,
      suppressHeaderMenuButton: true,
      resizable: false,
      lockPosition: true,
      lockVisible: true,
      maxWidth: 27, // 27 is the magic number which gives the col enough width to render the left padding and the entire checkbox
      headerClass: 'ag-header-custom-checkbox', // we need to do some padding fixups so we have our own class for this situation
      // Having the value be wrapped in an object is a hack to get change detection to run on every data update.
      // This change detection hack also propagates upward through the aggFunc below. This will make the rowClassRules re-apply
      // on every data update. Significant hack, but there are explicit tests to cover that this happen.
      valueGetter: (params: ValueGetterParams<MarketSelectorRow>): MarketSelectorCheckboxValue => {
        return { selected: params.node?.data?.selected ?? false };
      },
      initialAggFunc: MARKET_SELECTOR_CHECKBOX_AGG_FUNC,
      cellRenderer: MarketSelectorBlotterCheckboxCellRenderer,
      headerComponent: MarketSelectorBlotterHeaderComponent,
    } satisfies ColDef,
  },
  {
    type: 'market',
    id: 'market',
    field: 'item.market.Name',
    title: 'Markets',
    rowGroup: true,
    hide: true,
  },
] satisfies ColumnDef<MarketSelectorRow>[];

type ColumnContributionInputs = Pick<
  MarketSelectorColumnContribution,
  'columns' | 'simpleModeVisibleColIDs' | 'advancedModeVisibleColIDs'
>;

/**
 * We want to avoid pushing in new column defs into aggrid as much as possible. It leads to a lot of recomputation.
 * This hook simply put just wraps the columns in some basic memoization.
 */
function useStableColumnContributionInputs(
  contributions: MarketSelectorColumnContribution[]
): ColumnContributionInputs[] {
  const nextInputs = getColumnContributionInputs(contributions);
  const ref = useRef(nextInputs);
  if (!areColumnContributionInputsEqual(ref.current, nextInputs)) {
    ref.current = nextInputs;
  }
  return ref.current;
}

function getColumnContributionInputs(contributions: MarketSelectorColumnContribution[]): ColumnContributionInputs[] {
  return contributions.map(({ columns, simpleModeVisibleColIDs, advancedModeVisibleColIDs }) => ({
    columns,
    simpleModeVisibleColIDs,
    advancedModeVisibleColIDs,
  }));
}

function areColumnContributionInputsEqual(prev: ColumnContributionInputs[], next: ColumnContributionInputs[]) {
  if (prev.length !== next.length) {
    return false;
  }

  return prev.every((prevContribution, index) => {
    const nextContribution = next[index];
    return (
      areArraysEqual(prevContribution.columns, nextContribution.columns) &&
      areArraysEqual(prevContribution.simpleModeVisibleColIDs, nextContribution.simpleModeVisibleColIDs) &&
      areArraysEqual(prevContribution.advancedModeVisibleColIDs, nextContribution.advancedModeVisibleColIDs)
    );
  });
}

function areArraysEqual(a1: unknown[] | undefined, a2: unknown[] | undefined) {
  // We do the basic reference equality check, but then also are defensive against the implementer doing stuff like `?? []`,
  // as in recreating new empty arrays. Obviously two empty arrays, despite being different instances, are the same thing.
  if (a1 === a2) {
    return true;
  }

  // Treat undefined as empty both as length 0, same thing for us
  const len1 = a1?.length ?? 0;
  const len2 = a2?.length ?? 0;
  return len1 === 0 && len2 === 0;
}

function getContributionColumns(
  advancedMode: boolean,
  contributions: ColumnContributionInputs[]
): ColumnOrColumnGroup[] {
  const columns = contributions.flatMap(contribution => contribution.columns);
  const visibleColIDs = new Set(
    contributions.flatMap(
      contribution =>
        (advancedMode ? contribution.advancedModeVisibleColIDs : contribution.simpleModeVisibleColIDs) ?? EMPTY_ARRAY
    )
  );
  return columns.map(column => getColumnWithVisibility(column, visibleColIDs));
}

function getColumnWithVisibility(column: ColumnOrColumnGroup, visibleColIDs: Set<string>): ColumnOrColumnGroup {
  if (isColumnGroup(column)) {
    return {
      ...column,
      children: column.children.map(childColumn => ({
        ...childColumn,
        hide: !visibleColIDs.has(getAgGridColId(childColumn)),
      })),
    };
  }

  return { ...column, hide: !visibleColIDs.has(getAgGridColId(column)) };
}

function assertUniqueColumnIDs(columns: ColumnOrColumnGroup[]) {
  if (!import.meta.env.DEV) {
    return;
  }

  const columnIDs = new Set<string>();
  for (const column of extractColumns(columns)) {
    const columnID = getAgGridColId(column);
    if (columnIDs.has(columnID)) {
      throw new Error(`Duplicate Market Selector column ID: ${columnID}`);
    }
    columnIDs.add(columnID);
  }
}
